<script setup lang="ts">
import type { IconName } from '@ui/components/icon/types'
import type { SizesType } from '@ui/styles'
import type { ListItemType } from './ListTypes'
import { Sizes } from '@ui/styles'
import Check from '~icons/cue-regular/check'
import Icon from '../icon/Icon.vue'
import Switch from '../switch/Switch.vue'

const props = withDefaults(
  defineProps<{
    multiple?: boolean
    label?: string
    hint?: string
    icon?: IconName
    value?: ListItemType['value']
    flag?: boolean
    dark?: boolean
    disabled?: boolean
    selected?: boolean
    size?: SizesType
    variant?: 'label' | 'title' | 'button' | 'divider' | 'switch right'
    clickable?: boolean
  }>(),
  {
    multiple: false,
    label: '',
    hint: '',
    icon: '',
    value: '',
    flag: true,
    dark: false,
    disabled: false,
    selected: false,
    size: Sizes.Leia,
    variant: 'label',
    clickable: true,
  },
)

defineSlots<{
  left?: (props: { value: ListItemType['value']; checked: boolean }) => void
  default?: (props: { value: ListItemType['value']; label: string; hint: string; checked: boolean }) => void
  right?: (props: { value: ListItemType['value']; checked: boolean }) => void
}>()

const model = defineModel<ListItemType['value'][] | ListItemType['value'] | boolean>()

const isSwitchRight = computed(() => props.variant === 'switch right')

const checked = computed(() => {
  if (isSwitchRight.value) {
    return Boolean(model.value)
  }

  if (Array.isArray(model.value)) {
    return model.value.includes(props.value)
  }
  return model.value === props.value
})

const isSelectionChecked = computed(() => !isSwitchRight.value && checked.value)
const isSelected = computed(() => props.selected || isSelectionChecked.value)

function toggleSwitch(value = !checked.value) {
  if (props.disabled || !isSwitchRight.value) {
    return
  }

  model.value = value
}
</script>

<template lang="pug">
li.list-item.flex-between-center(
  :class="[size, variant, { selected: isSelected, checked: isSelectionChecked, dark, disabled, clickable: clickable && !disabled && variant !== 'divider' }]"
  @click="toggleSwitch()"
)
  template(v-if="variant === 'divider'")
    .divider.w-100p
  template(v-else)
    .content.flex-start-center.row-8.w-100p.min-w-0
      slot(name="left" :value="value" :checked="checked")
        Icon.icon(v-if="icon" :name="icon")
      input.list-item-input(
        v-if="multiple && !isSwitchRight"
        type="checkbox"
        :checked="checked"
        :value="model"
      )
      .label-wrap.min-w-0.flex-1
        slot(:value="value" :label="label" :hint="hint" :checked="checked")
          .text-wrap.flex.col-0.w-100p
            span.label-text.w-100p {{ $t(label) }}
            span.hint-text.w-100p(v-if="hint") {{ $t(hint) }}
    slot(name="right" :value="value" :checked="checked")
      Switch.switch-control(v-if="isSwitchRight" :modelValue="checked" :disabled="disabled" @update:modelValue="toggleSwitch" @click.stop)
    Icon.flag(v-if="flag && !multiple && checked && !isSwitchRight" :name="Check")
</template>

<style scoped src="../../styles/css/checkbox-input.css"></style>

<style scoped>
.list-item {
  --item-radius: 12px;
  --item-padding: 10px 12px;
  --item-typography: var(--uno-typ-14-20-400);
  transition: 0.2s ease;
  transition-property: color, background-color, box-shadow;
  border-radius: var(--item-radius);
  color: var(--cue-content-neutral-secondary);
  padding: var(--item-padding);

  .label-text {
    display: block;
  }

  .hint-text {
    color: var(--cue-content-neutral-secondary);
  }

  .icon,
  .flag {
    color: var(--cue-content-neutral-tertiary);
    flex-shrink: 0;
  }

  &.button {
    color: var(--cue-content-primary-default);
  }

  &.luke {
    --item-radius: 16px;

    &.label,
    &.button,
    &.switch.right,
    &.title {
      --item-padding: 12px;
    }

    &.label {
      --uno: typ-16-24-400;

      .hint-text {
        --uno: typ-14-20-400;
      }
    }

    &.title {
      --uno: typ-16-24-600;
    }

    &.button,
    &.switch.right {
      --uno: typ-16-24-400;
    }

    .icon,
    .flag {
      --uno: s-24;
    }
  }

  &.leia {
    --item-radius: 12px;

    &.label,
    &.title {
      --item-padding: 8px 10px;
    }

    &.button,
    &.switch.right {
      --item-padding: 8px 12px;
    }

    &.label {
      --uno: typ-14-20-400;

      .hint-text {
        --uno: typ-12-16-400;
      }
    }

    &.title {
      --uno: typ-14-20-600;
    }

    &.button,
    &.switch.right {
      --uno: typ-14-20-400;
    }

    .icon,
    .flag {
      --uno: s-20;
    }
  }

  &.r2d2 {
    --item-radius: 10px;

    &.label,
    &.title {
      --item-padding: 8px 12px;
    }

    &.button,
    &.switch.right {
      --item-padding: 7px 12px;
    }

    &.label {
      --uno: typ-12-18-400;

      .hint-text {
        --uno: typ-12-16-400;
      }
    }

    &.title {
      --uno: typ-14-20-600;
    }

    &.button,
    &.switch.right {
      --uno: typ-12-18-400;
    }

    .icon,
    .flag {
      --uno: s-16;
    }
  }

  &.divider {
    --uno: p-4-0;
    min-height: auto;
    background: transparent;

    .divider {
      border-bottom: 1px solid var(--cue-border-neutral-primary);
    }
  }

  &.switch.right {
    .content {
      width: auto;
      flex: 1;
    }

    .switch-control {
      flex-shrink: 0;
    }
  }

  &.selected,
  &.checked {
    color: var(--cue-content-primary-on-soft);
    background-color: var(--cue-bg-primary-soft);

    .hint-text {
      color: var(--cue-content-primary-on-soft);
    }

    .icon,
    .flag {
      color: var(--cue-content-primary-default);
    }
  }

  &.clickable {
    --uno: cursor-pointer;

    &:hover {
      background-color: var(--cue-bg-neutral-secondary);
      color: var(--cue-content-neutral-primary);

      .hint-text {
        color: var(--cue-content-neutral-secondary);
      }

      &.button {
        color: var(--cue-content-primary-hover);
      }

      .list-item-input {
        border-color: var(--cue-border-neutral-tertiary-hover);

        &:checked {
          background-color: var(--cue-bg-primary-default-hover);
        }
      }
    }

    &:active {
      background-color: var(--cue-bg-neutral-secondary-pressed);
    }

    &:focus-visible {
      outline: 6px solid var(--cue-border-primary-soft);
      box-shadow: 0 0 0 1px var(--cue-border-primary-soft);
    }
  }

  &.selected.clickable,
  &.checked.clickable {
    &:hover {
      background-color: var(--cue-bg-primary-soft-hover);
      color: var(--cue-content-primary-on-soft);

      .hint-text {
        color: var(--cue-content-primary-on-soft);
      }
    }

    &:active {
      background-color: var(--cue-bg-primary-soft-pressed);
    }
  }

  &.dark {
    color: var(--cue-content-neutral-on-inverse);

    .hint-text {
      color: var(--cue-content-neutral-on-inverse-secondary);
    }

    &.checked {
      color: var(--cue-content-neutral-on-inverse);
      background-color: var(--cue-bg-neutral-inverse);

      .hint-text {
        color: var(--cue-content-neutral-on-inverse-secondary);
      }
    }

    &:hover:not(.disabled) {
      color: var(--cue-content-neutral-on-inverse);
      background-color: var(--cue-bg-neutral-inverse-hover);

      .hint-text {
        color: var(--cue-content-neutral-on-inverse-secondary);
      }
    }
  }

  &.disabled {
    background: var(--cue-bg-neutral-disabled);
    color: var(--cue-content-neutral-disabled);
    cursor: not-allowed;
    pointer-events: none;

    .icon,
    .hint-text,
    .flag {
      color: var(--cue-content-neutral-disabled);
    }
  }
}
</style>
